/**
 * Bond Analysis — analyst-facing page for RJ Shape B partnership.
 *
 * Workflow (per Bobby 2026-05-05 design):
 *   1. RJ analyst drops the MSIA Property Detail CSV (per-deal export from
 *      MSIA Standard Reports → Property Detail → Download to Excel).
 *   2. Pastes a list of CUSIPs they want to evaluate for a bank.
 *   3. Picks the bank context (defaults to current team).
 *   4. Submits → backend runs ingest + OCS extract + CRA qualification
 *      pipeline per CUSIP, returns the analyst output table.
 *   5. Click a row → drill-down panel showing per-property reasoning.
 *
 * Day 6 MVP scope: data display only. Maps + side-by-side comparison +
 * pitch-PDF export are Day 7 polish.
 */

import { useState, useMemo, useEffect, type ChangeEvent, type FormEvent, type DragEvent } from 'react';
import { Link } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient, useQueries } from '@tanstack/react-query';
import {
  ArrowUpTrayIcon,
  CurrencyDollarIcon,
  MapPinIcon,
  CheckCircleIcon,
  ExclamationTriangleIcon,
  XCircleIcon,
  DocumentArrowDownIcon,
  ArrowPathIcon,
  BuildingOffice2Icon,
  BuildingLibraryIcon,
  PlusIcon,
  SparklesIcon,
  TableCellsIcon,
  GlobeAmericasIcon,
} from '@heroicons/react/24/outline';
import toast from 'react-hot-toast';

import { bondsApi, craApi } from '../lib/api';
import { useTeamId } from '../hooks/useTeamId';
import BondsResultsMap, {
  classifyMapStatus,
  type BondMapProperty,
  type BondMapStatus,
} from '../components/bonds/BondsResultsMap';

// ─── Types (mirror backend response shapes) ─────────────────────────────────

interface CusipLookupRow {
  cusip: string;
  status: 'OK' | 'NOT_FOUND' | 'ERROR';
  error?: string;
  totalQualifiedDollars?: number;
  propertiesInAa?: number;
  propertiesOutsideAa?: number;
  geocodingFailures?: number;
  affordabilityMissing?: number;
  // Set when the analyst pasted an unknown CUSIP and OpenFIGI auto-mapped
  // it to its deal during this lookup. Surfacing this lets RJ see the
  // magic-wand assist happened (vs. needing the manual Register modal).
  autoResolvedDealId?: string;
}

interface PropertyDetail {
  propertyId: string;
  propertyName: string | null;
  address: string | null;
  city: string | null;
  state: string | null;
  county: string | null;
  censusTract: string | null;
  latitude: number | null;
  longitude: number | null;
  insideAa: boolean;
  matchingAaName: string | null;
  units: number | null;
  lmiUnits: number | null;
  lmiPct: number | null;
  qualifiesForCra: boolean;
  allocatedDollars: number | null;
  reason: string;
}

interface QualificationDetail {
  cusip: string;
  bondName: string;
  totalQualifiedDollars: number;
  propertiesInAa: number;
  propertiesOutsideAa: number;
  totalUnitsInAa: number;
  totalLmiUnitsInAa: number;
  pctLmiUnitsInAa: number | null;
  properties: PropertyDetail[];
  geocodingFailures: number;
  affordabilityMissing: number;
}

// ─── Helpers ────────────────────────────────────────────────────────────────

function formatDollars(n: number | null | undefined): string {
  if (n == null) return '—';
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
    maximumFractionDigits: 0,
  }).format(n);
}

function formatPct(n: number | null | undefined): string {
  if (n == null) return '—';
  return `${(n * 100).toFixed(1)}%`;
}

function downloadCsv(filename: string, rows: string[][]) {
  const csv = rows.map(r =>
    r.map(cell => {
      if (cell == null) return '';
      const escaped = String(cell).replace(/"/g, '""');
      return /[",\n]/.test(escaped) ? `"${escaped}"` : escaped;
    }).join(','),
  ).join('\n');
  const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
  const url = URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = filename;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  URL.revokeObjectURL(url);
}

// ─── Component ──────────────────────────────────────────────────────────────

// Postal → 2-digit state FIPS, to default the AA picker to the pool's state.
const POSTAL_TO_FIPS: Record<string, string> = {
  AL: '01', AK: '02', AZ: '04', AR: '05', CA: '06', CO: '08', CT: '09', DE: '10', DC: '11',
  FL: '12', GA: '13', HI: '15', ID: '16', IL: '17', IN: '18', IA: '19', KS: '20', KY: '21',
  LA: '22', ME: '23', MD: '24', MA: '25', MI: '26', MN: '27', MS: '28', MO: '29', MT: '30',
  NE: '31', NV: '32', NH: '33', NJ: '34', NM: '35', NY: '36', NC: '37', ND: '38', OH: '39',
  OK: '40', OR: '41', PA: '42', RI: '44', SC: '45', SD: '46', TN: '47', TX: '48', UT: '49',
  VT: '50', VA: '51', WA: '53', WV: '54', WI: '55', WY: '56',
};

// Curated metro → county footprints, used for the AA-picker "quick-add" when the
// environment's census MSA data isn't loaded (e.g., staging). Falls back to live
// DB MSAs where available. County codes are 5-digit FIPS.
const STATE_METRO_PRESETS: Record<string, { name: string; counties: string[] }[]> = {
  '47': [
    { name: 'Nashville–Davidson–Murfreesboro MSA', counties: ['47015', '47021', '47037', '47043', '47081', '47111', '47119', '47147', '47149', '47159', '47165', '47169', '47187', '47189'] },
    { name: 'Knoxville MSA', counties: ['47001', '47009', '47013', '47057', '47093', '47105', '47129', '47145', '47173'] },
    { name: 'Chattanooga MSA (TN counties)', counties: ['47065', '47115', '47153'] },
    { name: 'Memphis MSA (TN counties)', counties: ['47157', '47047', '47167'] },
  ],
};

export default function BondsAnalysisPage() {
  const { teamId, isLoading: teamLoading } = useTeamId();
  const queryClient = useQueryClient();

  const [cusipText, setCusipText] = useState('');
  const [label, setLabel] = useState('');
  const [results, setResults] = useState<CusipLookupRow[] | null>(null);
  const [selectedCusip, setSelectedCusip] = useState<string | null>(null);
  const [drillDown, setDrillDown] = useState<QualificationDetail | null>(null);

  // Results-section view — Table is the analyst's default; Map is the
  // visual portfolio view (the "demo closer" the bank's CRA officer sees).
  const [resultsView, setResultsView] = useState<'table' | 'map'>('table');
  // Same toggle inside the per-bond drill-down panel.
  const [drillDownView, setDrillDownView] = useState<'table' | 'map'>('table');

  // Filter state — drives both the table and the map so analysts only
  // learn one mental model. Defaults: show everything.
  const ALL_STATUSES: BondMapStatus[] = ['qualifies', 'outside_aa', 'no_affordability', 'no_geocode'];
  const [filterStatuses, setFilterStatuses] = useState<Set<BondMapStatus>>(new Set(ALL_STATUSES));
  const [filterCusips, setFilterCusips] = useState<Set<string>>(new Set());
  const [filterMinLmi, setFilterMinLmi] = useState<number>(0);
  const [filterStates, setFilterStates] = useState<Set<string>>(new Set());
  // AA filter — when empty, all AAs are shown. When populated, only
  // properties matching one of these AAs are kept. Mirrors filterCusips.
  const [filterAaIds, setFilterAaIds] = useState<Set<string>>(new Set());

  // Register-CUSIP modal — shown when an analyst needs to manually map a
  // CUSIP to its Deal ID. Pre-fills the CUSIP if launched from a "Not
  // ingested" result row.
  const [registerModalOpen, setRegisterModalOpen] = useState(false);
  const [registerCusip, setRegisterCusip] = useState('');
  const [registerDealId, setRegisterDealId] = useState('');

  // Filename of the most recent successful upload per source. The toast
  // is transient — these power the persistent confirmation strips under
  // each dropzone so analysts can see at a glance what they've ingested
  // without scrolling away to the Bond Library.
  const [lastMsiaFileName, setLastMsiaFileName] = useState<string | null>(null);
  const [lastFannieFileName, setLastFannieFileName] = useState<string | null>(null);

  // Single-family CRA pool: the ingest result (pool key + summary) after upload,
  // which unlocks the independent-validation report download.
  const [sfPool, setSfPool] = useState<{
    cusip: string;
    dealName: string;
    loanCount: number;
    totalPrincipal: number;
    state: string | null;
    qualificationBasis: string | null;
  } | null>(null);

  // Engagement details for the SF validation report (client-facing header) +
  // an ad-hoc assessment area entered here for a contracted bank (no bank setup).
  const [sfEng, setSfEng] = useState({
    bankName: '', regulator: 'FDIC', cityState: '', contact: '', validationRef: '', preparedBy: '', fdicCert: '',
  });
  const [sfAaState, setSfAaState] = useState('');                       // state FIPS, e.g. '47'
  const [sfCounties, setSfCounties] = useState<Set<string>>(new Set()); // 5-digit county FIPS
  const [sfCountyFilter, setSfCountyFilter] = useState('');
  // County names from an FDIC pull, so multi-state chips show names even before
  // that state's county list has been loaded into the picker.
  const [sfCountyNameOverrides, setSfCountyNameOverrides] = useState<Record<string, string>>({});
  const setEng = (k: keyof typeof sfEng, v: string) => setSfEng(prev => ({ ...prev, [k]: v }));

  // State geography lists for the AA picker (reuse the CRA geography endpoints).
  const { data: sfStatesData, isError: sfStatesError, isLoading: sfStatesLoading, refetch: refetchStates } = useQuery({
    queryKey: ['sf-geo-states', teamId],
    queryFn: () => bondsApi.geographyStates(teamId!).then(r => r.data as { states: { code: string; name: string }[] }),
    enabled: !!teamId && !!sfPool,
  });
  const { data: sfMsasData } = useQuery({
    queryKey: ['sf-geo-msas', teamId, sfAaState],
    queryFn: () => bondsApi.geographyMsas(teamId!, sfAaState).then(r => r.data as { msas: { code: string; name: string }[] }),
    enabled: !!teamId && !!sfAaState,
  });
  const { data: sfCountiesData } = useQuery({
    queryKey: ['sf-geo-counties', teamId, sfAaState],
    queryFn: () => bondsApi.geographyCounties(teamId!, sfAaState).then(r => r.data as { counties: { code: string; name: string }[] }),
    enabled: !!teamId && !!sfAaState,
  });
  // Default the AA state to the pool's state once a pool is uploaded.
  useEffect(() => {
    if (sfPool?.state && !sfAaState) {
      const fips = POSTAL_TO_FIPS[sfPool.state.trim().toUpperCase()];
      if (fips) setSfAaState(fips);
    }
  }, [sfPool?.state, sfAaState]);
  // Add every county in a chosen MSA to the selection in one click.
  const addMsaCounties = async (msaCode: string) => {
    if (!msaCode || !teamId) return;
    const res = await bondsApi.geographyCounties(teamId, sfAaState, msaCode);
    const codes = (res.data as { counties: { code: string }[] }).counties.map(c => c.code);
    setSfCounties(prev => new Set([...prev, ...codes]));
  };
  const toggleCounty = (code: string) =>
    setSfCounties(prev => {
      const n = new Set(prev);
      if (n.has(code)) n.delete(code); else n.add(code);
      return n;
    });
  const addCounties = (codes: string[]) => setSfCounties(prev => new Set([...prev, ...codes]));
  // Metro quick-add options: prefer live DB MSAs; fall back to curated presets
  // when the environment's census MSA data isn't loaded.
  const sfMetroOptions = useMemo(() => {
    const db = sfMsasData?.msas ?? [];
    if (db.length) return db.map(m => ({ label: m.name, msaCode: m.code as string | null, counties: null as string[] | null }));
    return (STATE_METRO_PRESETS[sfAaState] ?? []).map(p => ({ label: p.name, msaCode: null, counties: p.counties }));
  }, [sfMsasData, sfAaState]);
  // Name lookup for the selected-county chips — the loaded state's counties plus
  // any names carried in from an FDIC pull (which can span multiple states).
  const sfCountyName = useMemo(() => {
    const m = new Map<string, string>(Object.entries(sfCountyNameOverrides));
    for (const c of sfCountiesData?.counties ?? []) m.set(c.code, c.name);
    return m;
  }, [sfCountiesData, sfCountyNameOverrides]);

  // Look up an institution by FDIC cert → pre-fill engagement details + a
  // proposed AA (the bank's branch-county footprint).
  const fdicLookupMutation = useMutation({
    mutationFn: (cert: string) => bondsApi.fdicLookup(teamId!, cert).then(r => r.data as {
      cert: number; name: string | null; city: string | null; state: string | null;
      regulator: string | null; counties: { code: string; name: string }[]; branchCount: number;
    }),
    onSuccess: (d) => {
      setSfEng(prev => ({
        ...prev,
        bankName: d.name ?? prev.bankName,
        cityState: d.city && d.state ? `${d.city}, ${d.state}` : prev.cityState,
        regulator: d.regulator ?? prev.regulator,
      }));
      setSfCountyNameOverrides(Object.fromEntries(d.counties.map(c => [c.code, c.name])));
      setSfCounties(new Set(d.counties.map(c => c.code)));
      // Focus the picker on the pool's state (or the footprint's first state).
      const poolFips = sfPool?.state ? POSTAL_TO_FIPS[sfPool.state.trim().toUpperCase()] : undefined;
      setSfAaState(poolFips ?? d.counties[0]?.code.slice(0, 2) ?? '');
      toast.success(`${d.name ?? 'Institution'} — ${d.counties.length} counties across ${d.branchCount} branches`);
    },
    onError: (err: any) => toast.error(err?.response?.data?.error || 'FDIC lookup failed — check the cert number'),
  });

  // Bank's geographic footprint — used as the map's fallback view when
  // the current analysis has no properties geocoded yet (which happens
  // on first paste before OCS extraction populates lat/long). Computed
  // once per team from active branches with lat/long; cached for the
  // session since the bank's footprint doesn't change mid-session.
  const { data: aaBoundsData } = useQuery({
    queryKey: ['cra-aa-bounds', teamId],
    queryFn: () => craApi.getAssessmentAreaBounds(teamId!).then(r => r.data as {
      areas: Array<{ id: string; name: string; bounds: [[number, number], [number, number]] | null; centroid: [number, number] | null; branchCount: number }>;
      overallBounds: [[number, number], [number, number]] | null;
      overallCentroid: [number, number] | null;
      totalBranches: number;
    }),
    enabled: !!teamId,
    staleTime: 10 * 60 * 1000,
  });

  // Recent lookups (for the sidebar).
  const { data: lookupsData } = useQuery({
    queryKey: ['bonds-lookups', teamId],
    queryFn: () => bondsApi.listLookups(teamId!, 10).then(r => r.data),
    enabled: !!teamId,
  });

  // ── MSIA CSV upload (Freddie) ─────────────────────────────────────────────
  const uploadMsiaMutation = useMutation({
    mutationFn: (file: File) => bondsApi.uploadMsiaCsv(teamId!, file).then(r => r.data),
    onSuccess: (data) => {
      const dealsLabel = data.deals === 1 ? 'deal' : 'deals';
      toast.success(
        `Freddie: Ingested ${data.properties} ${data.properties === 1 ? 'property' : 'properties'} ` +
        `across ${data.deals} ${dealsLabel} (${data.loans} loans).`,
      );
    },
    onError: (err: any) => {
      toast.error(err?.response?.data?.error || 'MSIA CSV upload failed');
    },
  });

  // ── Fannie DUS Disclose xlsx upload ──────────────────────────────────────
  const uploadFannieMutation = useMutation({
    mutationFn: (file: File) => bondsApi.uploadFannieXlsx(teamId!, file).then(r => r.data),
    onSuccess: (data) => {
      const propsLabel = data.properties === 1 ? 'property' : 'properties';
      toast.success(
        `Fannie: Ingested ${data.dealId} (${data.cusip}) — ` +
        `${data.properties} ${propsLabel}, ${data.affordabilityRowsUpserted} with affordability data.`,
      );
    },
    onError: (err: any) => {
      toast.error(err?.response?.data?.error || 'Fannie xlsx upload failed');
    },
  });

  // ── Single-family CRA pool upload + validation report ────────────────────
  const uploadSfMutation = useMutation({
    mutationFn: (file: File) => bondsApi.uploadSfPool(teamId!, file).then(r => r.data),
    onSuccess: (data) => {
      setSfPool(data);
      toast.success(
        `Single-family pool ingested: ${data.dealName} — ${data.loanCount} loans, ` +
        `$${Math.round(data.totalPrincipal).toLocaleString()}.`,
      );
    },
    onError: (err: any) => {
      toast.error(err?.response?.data?.error || 'Single-family pool upload failed');
    },
  });

  const sfReportMutation = useMutation({
    mutationFn: () => {
      if (!sfPool) throw new Error('Upload a single-family pool first');
      const countyCodes = [...sfCounties];
      return bondsApi.sfValidationPdf(teamId!, {
        cusip: sfPool.cusip,
        poolMeta: { state: sfPool.state, qualificationBasis: sfPool.qualificationBasis },
        bankName: sfEng.bankName.trim() || undefined,
        primaryRegulator: sfEng.regulator || undefined,
        institutionCityState: sfEng.cityState.trim() || undefined,
        fdicCert: sfEng.fdicCert.trim() || undefined,
        contact: sfEng.contact.trim() || undefined,
        validationRef: sfEng.validationRef.trim() || undefined,
        preparedBy: sfEng.preparedBy.trim() || undefined,
        assessmentArea: countyCodes.length
          ? { name: sfEng.bankName.trim() ? `${sfEng.bankName.trim()} assessment area` : 'Assessment area', countyCodes }
          : undefined,
      });
    },
    onSuccess: (res) => {
      const blob = res.data instanceof Blob ? res.data : new Blob([res.data], { type: 'application/pdf' });
      const url = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `clarid-cra-validation-${new Date().toISOString().slice(0, 10)}.pdf`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(url);
      toast.success('Validation report downloaded');
    },
    onError: (err: any) => {
      toast.error(err?.response?.data?.error || 'Validation report generation failed');
    },
  });

  // ── Bulk lookup ───────────────────────────────────────────────────────────
  const lookupMutation = useMutation({
    mutationFn: (payload: { cusips: string[]; label?: string }) =>
      bondsApi.bulkLookup(teamId!, payload).then(r => r.data),
    onSuccess: (data) => {
      setResults(data.cusipResults);
      queryClient.invalidateQueries({ queryKey: ['bonds-lookups'] });
      const ok = data.cusipResults.filter((r: CusipLookupRow) => r.status === 'OK').length;
      toast.success(`Analyzed ${ok}/${data.cusipResults.length} CUSIPs`);
    },
    onError: (err: any) => {
      toast.error(err?.response?.data?.error || 'Lookup failed');
    },
  });

  // ── Drill-down (single CUSIP qualification detail) ────────────────────────
  const drillMutation = useMutation({
    mutationFn: (cusip: string) =>
      bondsApi.qualifyCusip(teamId!, cusip).then(r => r.data),
    onSuccess: (data) => {
      setDrillDown(data);
    },
    onError: (err: any) => {
      toast.error(err?.response?.data?.error || 'Failed to load detail');
    },
  });

  // ── Pitch PDF generation ──────────────────────────────────────────────────
  const pitchPdfMutation = useMutation({
    mutationFn: () => {
      const okCusips = (results ?? [])
        .filter(r => r.status === 'OK')
        .map(r => r.cusip);
      return bondsApi.pitchPdf(teamId!, { cusips: okCusips });
    },
    onSuccess: (res) => {
      const blob = res.data instanceof Blob ? res.data : new Blob([res.data], { type: 'application/pdf' });
      const url = URL.createObjectURL(blob);
      const link = document.createElement('a');
      const stamp = new Date().toISOString().slice(0, 10);
      link.href = url;
      link.download = `clarid-bond-analysis-${stamp}.pdf`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(url);
      toast.success('Pitch PDF downloaded');
    },
    onError: (err: any) => {
      toast.error(err?.response?.data?.error || 'Pitch PDF generation failed');
    },
  });

  // ── Portfolio map data: full property details for every OK CUSIP ────────
  // The map view needs lat/long + per-property qualification. The /qualify
  // endpoint already returns this for one CUSIP, so we fan out in parallel
  // and flatten. Only fires when the Map tab is selected — table-only
  // analysts pay no cost.
  const okCusips = useMemo(
    () => (results ?? []).filter(r => r.status === 'OK').map(r => r.cusip),
    [results],
  );
  const portfolioQualifyQueries = useQueries({
    queries: okCusips.map((cusip) => ({
      queryKey: ['bond-portfolio-qualify', teamId, cusip],
      queryFn: () => bondsApi.qualifyCusip(teamId!, cusip).then(r => r.data as QualificationDetail),
      enabled: !!teamId && resultsView === 'map' && okCusips.length > 0,
      staleTime: 5 * 60 * 1000,
    })),
  });
  const portfolioLoading = portfolioQualifyQueries.some(q => q.isLoading);

  // Flatten all bonds' properties into one BondMapProperty array, tagged
  // with the cusip + bondName so popups can show which bond it rolls up to.
  const allMapProperties: BondMapProperty[] = useMemo(() => {
    const flat: BondMapProperty[] = [];
    for (const q of portfolioQualifyQueries) {
      const bond = q.data;
      if (!bond) continue;
      for (const p of bond.properties) {
        flat.push({
          ...p,
          cusip: bond.cusip,
          bondName: bond.bondName,
        });
      }
    }
    return flat;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [portfolioQualifyQueries.map(q => q.dataUpdatedAt).join('|')]);

  // Set of US states present in the loaded portfolio — drives the state
  // filter dropdown so we only surface relevant options.
  const availableStates = useMemo(() => {
    const set = new Set<string>();
    for (const p of allMapProperties) if (p.state) set.add(p.state);
    return Array.from(set).sort();
  }, [allMapProperties]);

  // Apply shared filters — same predicate drives both views.
  const filteredMapProperties = useMemo(() => {
    return allMapProperties.filter(p => {
      const status = classifyMapStatus(p);
      if (!filterStatuses.has(status)) return false;
      if (filterCusips.size > 0 && !filterCusips.has(p.cusip)) return false;
      if (filterStates.size > 0 && (!p.state || !filterStates.has(p.state))) return false;
      if (filterMinLmi > 0 && (p.lmiPct == null || p.lmiPct * 100 < filterMinLmi)) return false;
      if (filterAaIds.size > 0) {
        // Properties carry matchingAaName but not matchingAaId; resolve
        // by name against the AA bounds list. Acceptable for filter
        // purposes since AA names are unique within a team.
        const aa = aaBoundsData?.areas.find(a => a.name === p.matchingAaName);
        if (!aa || !filterAaIds.has(aa.id)) return false;
      }
      return true;
    });
  }, [allMapProperties, filterStatuses, filterCusips, filterStates, filterMinLmi, filterAaIds, aaBoundsData]);

  // Compute the map fallback bounds — when no properties geocoded, fit
  // to the union of selected AAs (or the bank's overall footprint).
  const mapFallbackBounds = useMemo<[[number, number], [number, number]] | null>(() => {
    if (!aaBoundsData) return null;
    if (filterAaIds.size === 0) return aaBoundsData.overallBounds;
    // Union the selected AAs' bounds.
    let minLat = 90, maxLat = -90, minLng = 180, maxLng = -180;
    let any = false;
    for (const a of aaBoundsData.areas) {
      if (!filterAaIds.has(a.id) || !a.bounds) continue;
      const [[swLat, swLng], [neLat, neLng]] = a.bounds;
      if (swLat < minLat) minLat = swLat;
      if (neLat > maxLat) maxLat = neLat;
      if (swLng < minLng) minLng = swLng;
      if (neLng > maxLng) maxLng = neLng;
      any = true;
    }
    return any ? [[minLat, minLng], [maxLat, maxLng]] : aaBoundsData.overallBounds;
  }, [aaBoundsData, filterAaIds]);

  // Filtered CUSIP set drives the table view too: when a filter excludes
  // every property of a bond, hide that bond's row from the table.
  const filteredResults = useMemo(() => {
    if (!results) return null;
    // If no filters narrow things, return as-is — avoids hiding NOT_FOUND/ERROR
    // rows the analyst still needs to see.
    const filtersActive =
      filterStatuses.size !== ALL_STATUSES.length
      || filterCusips.size > 0
      || filterStates.size > 0
      || filterMinLmi > 0;
    if (!filtersActive || allMapProperties.length === 0) return results;

    // Bonds with at least one property surviving the filter stay visible.
    const cusipsWithProps = new Set(filteredMapProperties.map(p => p.cusip));
    return results.filter(r => r.status !== 'OK' || cusipsWithProps.has(r.cusip));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [results, filteredMapProperties, allMapProperties.length, filterStatuses, filterCusips, filterStates, filterMinLmi]);

  // Helper toggles for the filter chips.
  const toggleStatus = (s: BondMapStatus) => {
    setFilterStatuses(prev => {
      const next = new Set(prev);
      if (next.has(s)) next.delete(s); else next.add(s);
      return next;
    });
  };
  const toggleCusipFilter = (c: string) => {
    setFilterCusips(prev => {
      const next = new Set(prev);
      if (next.has(c)) next.delete(c); else next.add(c);
      return next;
    });
  };
  const toggleAaFilter = (id: string) => {
    setFilterAaIds(prev => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id); else next.add(id);
      return next;
    });
  };
  const resetFilters = () => {
    setFilterStatuses(new Set(ALL_STATUSES));
    setFilterCusips(new Set());
    setFilterStates(new Set());
    setFilterMinLmi(0);
    setFilterAaIds(new Set());
  };
  const filtersActive =
    filterStatuses.size !== ALL_STATUSES.length
    || filterCusips.size > 0
    || filterStates.size > 0
    || filterMinLmi > 0
    || filterAaIds.size > 0;

  // ── Register CUSIP → Deal mapping ────────────────────────────────────────
  // Used when the result table shows "Not ingested" and the analyst needs
  // to wire up the CUSIP. Backend creates Deal + Bond and (for WN-series)
  // immediately fetches the OCS PDF so the analyst doesn't need a separate
  // upload step.
  const registerMutation = useMutation({
    mutationFn: () =>
      bondsApi.registerBond(teamId!, {
        cusip: registerCusip.trim().toUpperCase(),
        dealId: registerDealId.trim(),
      }).then(r => r.data),
    onSuccess: (data) => {
      const ocsStatus = data.ocsExtraction?.status ?? 'SKIPPED';
      const ocsLine =
        ocsStatus === 'PARSED' ? ' OCS prospectus parsed.'
        : ocsStatus === 'FAILED' ? ' OCS extraction failed — check the deal ID.'
        : ocsStatus === 'NOT_APPLICABLE' ? ' OCS extraction not applicable for this series.'
        : '';
      toast.success(`Registered ${data.bond.cusip} → ${data.bond.dealId}.${ocsLine}`);
      setRegisterModalOpen(false);
      setRegisterDealId('');
      // Re-run the bulk lookup so the row flips from "Not ingested" → "OK".
      if (results && results.length > 0) {
        lookupMutation.mutate({
          cusips: results.map(r => r.cusip),
          label: label.trim() || undefined,
        });
      }
    },
    onError: (err: any) => {
      toast.error(err?.response?.data?.error || 'Registration failed');
    },
  });

  // ── Reload a past lookup ──────────────────────────────────────────────────
  const reloadMutation = useMutation({
    mutationFn: (lookupId: string) =>
      bondsApi.getLookup(teamId!, lookupId).then(r => r.data),
    onSuccess: (lookup: any) => {
      // The lookup row stores cusipResults JSON exactly as returned by the
      // bulk-lookup endpoint. Restore it into the results table.
      if (Array.isArray(lookup.cusipResults)) {
        setResults(lookup.cusipResults);
        setSelectedCusip(null);
        setDrillDown(null);
        if (lookup.label) setLabel(lookup.label);
        toast.success(`Loaded "${lookup.label || `${lookup.cusips.length} CUSIPs`}"`);
      } else {
        toast.error('This lookup has no stored results');
      }
    },
    onError: (err: any) => {
      toast.error(err?.response?.data?.error || 'Failed to load past lookup');
    },
  });

  // ── Handlers ──────────────────────────────────────────────────────────────
  const handleMsiaFileChange = (e: ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      setLastMsiaFileName(file.name);
      uploadMsiaMutation.mutate(file);
    }
    e.target.value = '';
  };

  const handleMsiaDrop = (e: DragEvent<HTMLLabelElement>) => {
    e.preventDefault();
    const file = e.dataTransfer.files?.[0];
    if (file) {
      setLastMsiaFileName(file.name);
      uploadMsiaMutation.mutate(file);
    }
  };

  const handleFannieFileChange = (e: ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      setLastFannieFileName(file.name);
      uploadFannieMutation.mutate(file);
    }
    e.target.value = '';
  };

  const handleFannieDrop = (e: DragEvent<HTMLLabelElement>) => {
    e.preventDefault();
    const file = e.dataTransfer.files?.[0];
    if (file) {
      setLastFannieFileName(file.name);
      uploadFannieMutation.mutate(file);
    }
  };

  const handleSfFileChange = (e: ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) uploadSfMutation.mutate(file);
    e.target.value = '';
  };

  const handleSfDrop = (e: DragEvent<HTMLLabelElement>) => {
    e.preventDefault();
    const file = e.dataTransfer.files?.[0];
    if (file) uploadSfMutation.mutate(file);
  };

  const handleSubmit = (e: FormEvent) => {
    e.preventDefault();
    const cusips = cusipText
      .split(/[\s,;]+/)
      .map(s => s.trim().toUpperCase())
      .filter(s => /^[0-9A-Z]{9}$/.test(s));
    if (cusips.length === 0) {
      toast.error('Paste at least one valid 9-character CUSIP');
      return;
    }
    setSelectedCusip(null);
    setDrillDown(null);
    lookupMutation.mutate({ cusips, label: label.trim() || undefined });
  };

  const handleSelectRow = (cusip: string) => {
    if (selectedCusip === cusip) {
      setSelectedCusip(null);
      setDrillDown(null);
      return;
    }
    setSelectedCusip(cusip);
    drillMutation.mutate(cusip);
  };

  const handleExportCsv = () => {
    if (!results || results.length === 0) return;
    // Map cusip → bondName from the portfolio queries (loaded when map view
    // is touched). Falls back to empty string so the file is still useful
    // even if the analyst exports straight from the table view.
    const bondNameByCusip = new Map<string, string>();
    for (const q of portfolioQualifyQueries) {
      if (q.data) bondNameByCusip.set(q.data.cusip, q.data.bondName);
    }
    const rows: string[][] = [
      ['CUSIP', 'Bond Name', 'Deal ID', 'Status', 'Qualified $', 'Properties In AA', 'Properties Outside AA', 'Geocoding Failures', 'Affordability Missing', 'Error'],
      ...results.map(r => [
        r.cusip,
        bondNameByCusip.get(r.cusip) ?? '',
        r.autoResolvedDealId ?? '',
        r.status,
        r.totalQualifiedDollars != null ? String(r.totalQualifiedDollars) : '',
        r.propertiesInAa != null ? String(r.propertiesInAa) : '',
        r.propertiesOutsideAa != null ? String(r.propertiesOutsideAa) : '',
        r.geocodingFailures != null ? String(r.geocodingFailures) : '',
        r.affordabilityMissing != null ? String(r.affordabilityMissing) : '',
        r.error ?? '',
      ]),
    ];
    const stamp = new Date().toISOString().slice(0, 10);
    downloadCsv(`bond-analysis-${stamp}.csv`, rows);
  };

  // Per-property CSV export — the artifact a CRA officer actually wants:
  // one row per property with location, AA match, units/LMI, qualifying
  // reason. Requires the Map tab to have been visited at least once
  // (portfolio queries populate then); we surface this as a separate
  // explicit button so the analyst opts in.
  const handleExportPropertiesCsv = () => {
    if (allMapProperties.length === 0) return;
    const rows: string[][] = [
      ['CUSIP', 'Bond Name', 'Property', 'Address', 'City', 'State', 'County', 'Census Tract', 'Inside AA', 'Matching AA', 'Units', 'LMI Units', 'LMI %', 'Qualifies', 'Allocated $', 'Reason'],
      ...allMapProperties.map(p => [
        p.cusip,
        p.bondName ?? '',
        p.propertyName ?? '',
        p.address ?? '',
        p.city ?? '',
        p.state ?? '',
        p.county ?? '',
        p.censusTract ?? '',
        p.insideAa ? 'Yes' : 'No',
        p.matchingAaName ?? '',
        p.units != null ? String(p.units) : '',
        p.lmiUnits != null ? String(p.lmiUnits) : '',
        p.lmiPct != null ? (p.lmiPct * 100).toFixed(1) : '',
        p.qualifiesForCra ? 'Yes' : 'No',
        p.allocatedDollars != null ? String(p.allocatedDollars) : '',
        p.reason,
      ]),
    ];
    const stamp = new Date().toISOString().slice(0, 10);
    downloadCsv(`bond-properties-${stamp}.csv`, rows);
  };

  // ── Sorted & summed totals ────────────────────────────────────────────────
  const totals = useMemo(() => {
    if (!results) return null;
    const ok = results.filter(r => r.status === 'OK');
    const totalQualified = ok.reduce((s, r) => s + (r.totalQualifiedDollars ?? 0), 0);
    const totalInAa = ok.reduce((s, r) => s + (r.propertiesInAa ?? 0), 0);
    const totalOutsideAa = ok.reduce((s, r) => s + (r.propertiesOutsideAa ?? 0), 0);
    return {
      bonds: results.length,
      okBonds: ok.length,
      totalQualified,
      totalInAa,
      totalOutsideAa,
    };
  }, [results]);

  if (teamLoading) {
    return <div className="animate-pulse h-64 bg-gray-200 rounded-lg" />;
  }

  return (
    <div className="space-y-6">
      {/* ─── Header ─── */}
      <div className="flex items-start justify-between">
        <div>
          <h1 className="text-2xl font-bold text-navy-900 flex items-center gap-2">
            <BuildingOffice2Icon className="h-7 w-7 text-teal-600" />
            Bond Analysis
          </h1>
          <p className="text-gray-600 mt-1">
            Paste CUSIPs to analyze CRA-investment qualification across the bank's assessment areas.
          </p>
        </div>
        <Link
          to="/bonds/library"
          className="inline-flex items-center gap-1.5 text-sm font-semibold text-teal-700 hover:text-teal-900 px-3 py-2 rounded-md hover:bg-teal-50 transition-colors"
        >
          <BuildingLibraryIcon className="h-4 w-4" />
          Bond Library
        </Link>
      </div>

      {/* ─── Step 1: Upload source data ─── */}
      <section className="bg-white rounded-2xl border border-gray-200 p-6">
        <h2 className="text-lg font-semibold text-navy-900 mb-1">
          1. Upload source data
        </h2>
        <p className="text-sm text-gray-600 mb-4">
          Multifamily: drop a Freddie MSIA Property Detail CSV or a Fannie DUS Disclose Detail Report xlsx. Single-family: drop an MBS CRA-targeted pool xlsx for independent validation. Re-uploads update existing records.
        </p>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          {/* Freddie MSIA */}
          <div className="flex flex-col gap-2">
            <label
              htmlFor="msia-csv-upload"
              onDrop={handleMsiaDrop}
              onDragOver={e => e.preventDefault()}
              className={`flex flex-col items-center justify-center border-2 border-dashed rounded-lg p-6 cursor-pointer transition-colors ${
                uploadMsiaMutation.data && !uploadMsiaMutation.isPending
                  ? 'border-green-300 bg-green-50/40 hover:border-green-400'
                  : 'border-gray-300 hover:border-teal-400 hover:bg-teal-50/30'
              }`}
            >
              {uploadMsiaMutation.data && !uploadMsiaMutation.isPending ? (
                <CheckCircleIcon className="h-8 w-8 text-green-600 mb-2" />
              ) : (
                <ArrowUpTrayIcon className="h-8 w-8 text-gray-400 mb-2" />
              )}
              <p className="text-sm font-semibold text-navy-900">Freddie MSIA · Property Detail CSV</p>
              <p className="text-xs text-gray-500 mt-1">
                {uploadMsiaMutation.isPending
                  ? 'Uploading…'
                  : uploadMsiaMutation.data
                    ? 'Drop another to replace, or click to browse'
                    : 'Drop .csv here or click to browse'}
              </p>
              <input
                id="msia-csv-upload"
                type="file"
                accept=".csv"
                className="sr-only"
                onChange={handleMsiaFileChange}
                disabled={uploadMsiaMutation.isPending}
              />
            </label>
            {uploadMsiaMutation.data && !uploadMsiaMutation.isPending && (
              <div className="flex items-start gap-2 px-3 py-2 rounded-lg bg-green-50 border border-green-200 text-xs">
                <CheckCircleIcon className="h-4 w-4 text-green-600 flex-shrink-0 mt-0.5" />
                <div className="flex-1 min-w-0">
                  <div className="font-semibold text-green-900 truncate" title={lastMsiaFileName ?? ''}>
                    {lastMsiaFileName ?? 'Upload received'}
                  </div>
                  <div className="text-green-800">
                    {uploadMsiaMutation.data.properties} {uploadMsiaMutation.data.properties === 1 ? 'property' : 'properties'} ·{' '}
                    {uploadMsiaMutation.data.deals} {uploadMsiaMutation.data.deals === 1 ? 'deal' : 'deals'} ·{' '}
                    {uploadMsiaMutation.data.loans} {uploadMsiaMutation.data.loans === 1 ? 'loan' : 'loans'}
                  </div>
                </div>
              </div>
            )}
          </div>

          {/* Fannie DUS Disclose */}
          <div className="flex flex-col gap-2">
            <label
              htmlFor="fannie-xlsx-upload"
              onDrop={handleFannieDrop}
              onDragOver={e => e.preventDefault()}
              className={`flex flex-col items-center justify-center border-2 border-dashed rounded-lg p-6 cursor-pointer transition-colors ${
                uploadFannieMutation.data && !uploadFannieMutation.isPending
                  ? 'border-green-300 bg-green-50/40 hover:border-green-400'
                  : 'border-gray-300 hover:border-teal-400 hover:bg-teal-50/30'
              }`}
            >
              {uploadFannieMutation.data && !uploadFannieMutation.isPending ? (
                <CheckCircleIcon className="h-8 w-8 text-green-600 mb-2" />
              ) : (
                <ArrowUpTrayIcon className="h-8 w-8 text-gray-400 mb-2" />
              )}
              <p className="text-sm font-semibold text-navy-900">Fannie DUS Disclose · Detail Report</p>
              <p className="text-xs text-gray-500 mt-1">
                {uploadFannieMutation.isPending
                  ? 'Uploading…'
                  : uploadFannieMutation.data
                    ? 'Drop another to replace, or click to browse'
                    : 'Drop .xlsx here or click to browse'}
              </p>
              <input
                id="fannie-xlsx-upload"
                type="file"
                accept=".xlsx,.xls"
                className="sr-only"
                onChange={handleFannieFileChange}
                disabled={uploadFannieMutation.isPending}
              />
            </label>
            {uploadFannieMutation.data && !uploadFannieMutation.isPending && (
              <div className="flex items-start gap-2 px-3 py-2 rounded-lg bg-green-50 border border-green-200 text-xs">
                <CheckCircleIcon className="h-4 w-4 text-green-600 flex-shrink-0 mt-0.5" />
                <div className="flex-1 min-w-0">
                  <div className="font-semibold text-green-900 truncate" title={lastFannieFileName ?? ''}>
                    {lastFannieFileName ?? 'Upload received'}
                  </div>
                  <div className="text-green-800">
                    {uploadFannieMutation.data.dealId}
                    {uploadFannieMutation.data.cusip ? ` · CUSIP ${uploadFannieMutation.data.cusip}` : ''} ·{' '}
                    {uploadFannieMutation.data.properties} {uploadFannieMutation.data.properties === 1 ? 'property' : 'properties'}
                    {typeof uploadFannieMutation.data.affordabilityRowsUpserted === 'number' && (
                      <> · {uploadFannieMutation.data.affordabilityRowsUpserted} with affordability</>
                    )}
                  </div>
                </div>
              </div>
            )}
          </div>

          {/* Single-Family CRA Pool (MBS) — independent validation */}
          <div className="flex flex-col gap-2">
            <label
              htmlFor="sf-pool-upload"
              onDrop={handleSfDrop}
              onDragOver={e => e.preventDefault()}
              className={`flex flex-col items-center justify-center border-2 border-dashed rounded-lg p-6 cursor-pointer transition-colors ${
                sfPool && !uploadSfMutation.isPending
                  ? 'border-green-300 bg-green-50/40 hover:border-green-400'
                  : 'border-gray-300 hover:border-teal-400 hover:bg-teal-50/30'
              }`}
            >
              {sfPool && !uploadSfMutation.isPending ? (
                <CheckCircleIcon className="h-8 w-8 text-green-600 mb-2" />
              ) : (
                <ArrowUpTrayIcon className="h-8 w-8 text-gray-400 mb-2" />
              )}
              <p className="text-sm font-semibold text-navy-900">Single-Family CRA Pool · MBS</p>
              <p className="text-xs text-gray-500 mt-1">
                {uploadSfMutation.isPending
                  ? 'Uploading…'
                  : sfPool
                    ? 'Drop another to replace, or click to browse'
                    : 'Drop the pool .xlsx here or click to browse'}
              </p>
              <input
                id="sf-pool-upload"
                type="file"
                accept=".xlsx,.xls"
                className="sr-only"
                onChange={handleSfFileChange}
                disabled={uploadSfMutation.isPending}
              />
            </label>
            {sfPool && !uploadSfMutation.isPending && (
              <div className="flex flex-col gap-3 px-3 py-3 rounded-lg bg-green-50 border border-green-200 text-xs">
                <div className="flex items-start gap-2">
                  <CheckCircleIcon className="h-4 w-4 text-green-600 flex-shrink-0 mt-0.5" />
                  <div className="flex-1 min-w-0">
                    <div className="font-semibold text-green-900 truncate" title={sfPool.dealName}>
                      {sfPool.dealName}
                    </div>
                    <div className="text-green-800">
                      {sfPool.loanCount} loans · ${Math.round(sfPool.totalPrincipal).toLocaleString()}
                      {sfPool.state ? ` · ${sfPool.state}` : ''}
                    </div>
                  </div>
                </div>

                {/* Bank & engagement details → client-facing report header. */}
                <div className="flex flex-col gap-2 pt-2 border-t border-green-200">
                  <p className="font-semibold text-navy-900">Bank &amp; engagement details</p>
                  {/* FDIC cert lookup → pre-fills name, city, regulator + a proposed AA. */}
                  <div className="flex gap-2 items-center">
                    <input value={sfEng.fdicCert} onChange={e => setEng('fdicCert', e.target.value)}
                      placeholder="FDIC cert # (optional)" className="flex-1 rounded border border-gray-300 px-2 py-1" />
                    <button type="button"
                      onClick={() => sfEng.fdicCert.trim() && fdicLookupMutation.mutate(sfEng.fdicCert.trim())}
                      disabled={fdicLookupMutation.isPending || !sfEng.fdicCert.trim()}
                      className="rounded-md bg-navy-700 px-3 py-1.5 font-semibold text-white hover:bg-navy-800 disabled:opacity-50">
                      {fdicLookupMutation.isPending ? 'Looking up…' : 'Look up'}
                    </button>
                  </div>
                  <p className="text-[11px] text-gray-500 -mt-1">
                    Pulls the institution's name, regulator, and a proposed assessment area from its branch footprint — confirm/edit below.
                  </p>
                  <div className="grid grid-cols-2 gap-2">
                    <input value={sfEng.bankName} onChange={e => setEng('bankName', e.target.value)}
                      placeholder="Institution name (e.g., First Community Bank, N.A.)"
                      className="col-span-2 rounded border border-gray-300 px-2 py-1" />
                    <select value={sfEng.regulator} onChange={e => setEng('regulator', e.target.value)}
                      className="rounded border border-gray-300 px-2 py-1 bg-white" title="Primary regulator — sets the CRA citation">
                      <option value="FDIC">FDIC-supervised</option>
                      <option value="OCC">OCC (national bank / “N.A.”)</option>
                      <option value="FED">Federal Reserve</option>
                    </select>
                    <input value={sfEng.cityState} onChange={e => setEng('cityState', e.target.value)}
                      placeholder="City, ST" className="rounded border border-gray-300 px-2 py-1" />
                    <input value={sfEng.contact} onChange={e => setEng('contact', e.target.value)}
                      placeholder="Attn: contact (optional)" className="col-span-2 rounded border border-gray-300 px-2 py-1" />
                    <input value={sfEng.validationRef} onChange={e => setEng('validationRef', e.target.value)}
                      placeholder="Validation ref (optional)" className="rounded border border-gray-300 px-2 py-1" />
                    <input value={sfEng.preparedBy} onChange={e => setEng('preparedBy', e.target.value)}
                      placeholder="Prepared by (optional)" className="rounded border border-gray-300 px-2 py-1" />
                  </div>

                  {/* Assessment area — the bank's delineated counties. */}
                  <p className="font-semibold text-navy-900 mt-1">
                    Assessment area{' '}
                    <span className="font-normal text-gray-500">({sfCounties.size} counties selected)</span>
                  </p>
                  <div className="flex flex-col gap-2">
                    <select value={sfAaState}
                      onChange={e => { setSfAaState(e.target.value); setSfCountyFilter(''); }}
                      className="w-full rounded border border-gray-300 px-2 py-1 bg-white">
                      <option value="">{sfStatesLoading ? 'Loading states…' : 'Select state…'}</option>
                      {(sfStatesData?.states ?? []).map(s => <option key={s.code} value={s.code}>{s.name}</option>)}
                    </select>
                    {sfStatesError && (
                      <p className="text-[11px] text-red-600">
                        Couldn't load the geography list.{' '}
                        <button type="button" onClick={() => void refetchStates()} className="underline">Retry</button>
                      </p>
                    )}
                    {!sfStatesError && !sfStatesLoading && (sfStatesData?.states?.length ?? 0) === 0 && (
                      <p className="text-[11px] text-amber-700">No states returned — census reference data may not be loaded for this environment.</p>
                    )}
                    {sfAaState && sfMetroOptions.length > 0 && (
                      <select value="" onChange={e => {
                        const opt = sfMetroOptions[Number(e.target.value)];
                        if (!opt) return;
                        if (opt.counties) addCounties(opt.counties);
                        else if (opt.msaCode) void addMsaCounties(opt.msaCode);
                      }} className="w-full rounded border border-gray-300 px-2 py-1 bg-white">
                        <option value="">Quick-add a metro area…</option>
                        {sfMetroOptions.map((m, i) => <option key={i} value={i}>{m.label}</option>)}
                      </select>
                    )}
                    {sfAaState && (
                      <>
                        <input value={sfCountyFilter} onChange={e => setSfCountyFilter(e.target.value)}
                          placeholder="Filter counties…" className="w-full rounded border border-gray-300 px-2 py-1" />
                        <div className="max-h-40 overflow-y-auto rounded border border-gray-200 bg-white p-1">
                          {(sfCountiesData?.counties ?? [])
                            .filter(c => c.name.toLowerCase().includes(sfCountyFilter.toLowerCase()))
                            .map(c => (
                              <label key={c.code} className="flex items-center gap-2 px-1 py-0.5 hover:bg-gray-50 cursor-pointer">
                                <input type="checkbox" checked={sfCounties.has(c.code)} onChange={() => toggleCounty(c.code)} />
                                <span>{c.name}</span>
                              </label>
                            ))}
                        </div>
                      </>
                    )}
                    {/* Selected counties as removable chips. */}
                    {sfCounties.size > 0 && (
                      <div className="flex flex-wrap gap-1 items-center">
                        {[...sfCounties].map(code => (
                          <span key={code} className="inline-flex items-center gap-1 rounded-full bg-teal-100 text-teal-800 px-2 py-0.5">
                            {sfCountyName.get(code) ?? code}
                            <button type="button" onClick={() => toggleCounty(code)}
                              className="text-teal-600 hover:text-teal-900 font-bold" aria-label="Remove county">×</button>
                          </span>
                        ))}
                        <button type="button" onClick={() => setSfCounties(new Set())}
                          className="text-teal-700 hover:underline ml-1">Clear all</button>
                      </div>
                    )}
                  </div>
                </div>

                <button
                  type="button"
                  onClick={() => sfReportMutation.mutate()}
                  disabled={sfReportMutation.isPending}
                  className="inline-flex items-center justify-center gap-1.5 rounded-md bg-teal-600 px-3 py-1.5 text-xs font-semibold text-white hover:bg-teal-700 disabled:opacity-60"
                >
                  {sfReportMutation.isPending ? 'Generating…' : 'Download independent validation report (PDF)'}
                </button>
                {sfCounties.size === 0 && (
                  <p className="text-[11px] text-amber-700">
                    No assessment area selected — the report will show $0 qualifying for this bank. Pick the bank's counties above.
                  </p>
                )}
              </div>
            )}
          </div>
        </div>
      </section>

      {/* ─── Step 2: Paste CUSIPs ─── */}
      <section className="bg-white rounded-2xl border border-gray-200 p-6">
        <h2 className="text-lg font-semibold text-navy-900 mb-1">
          2. Paste CUSIPs to analyze
        </h2>
        <p className="text-sm text-gray-600 mb-4">
          One CUSIP per line, or comma/space-separated. Unknown CUSIPs are auto-mapped to
          their deals via OpenFIGI — no manual Deal-ID lookup. We'll then pull the OCS
          prospectus, geocode every property, and compute CRA qualification against the
          bank's assessment areas.
        </p>
        <form onSubmit={handleSubmit} className="space-y-3">
          <textarea
            value={cusipText}
            onChange={e => setCusipText(e.target.value)}
            placeholder="3132XK7D3&#10;3142FEDU1&#10;3132XF7H5"
            rows={6}
            className="w-full rounded-lg border border-gray-300 px-3 py-2 font-mono text-sm focus:outline-none focus:ring-2 focus:ring-teal-500 focus:border-teal-500"
          />
          <div className="flex flex-col sm:flex-row gap-3">
            <input
              type="text"
              value={label}
              onChange={e => setLabel(e.target.value)}
              placeholder="Label this analysis (optional, e.g., 'First Community pitch — Q2')"
              className="flex-1 rounded-lg border border-gray-300 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-teal-500 focus:border-teal-500"
              maxLength={200}
            />
            <button
              type="submit"
              disabled={lookupMutation.isPending || !cusipText.trim()}
              className="bg-teal-600 hover:bg-teal-700 disabled:opacity-50 disabled:cursor-not-allowed text-white px-6 py-2 rounded-lg font-semibold whitespace-nowrap transition-colors"
            >
              {lookupMutation.isPending ? (
                <span className="flex items-center gap-2">
                  <ArrowPathIcon className="h-4 w-4 animate-spin" />
                  Analyzing…
                </span>
              ) : 'Analyze CUSIPs'}
            </button>
          </div>
        </form>
      </section>

      {/* ─── Step 3: Results ─── */}
      {results && (
        <section className="bg-white rounded-2xl border border-gray-200 p-6">
          <div className="flex items-start justify-between mb-4">
            <div>
              <h2 className="text-lg font-semibold text-navy-900">3. Analysis results</h2>
              {totals && (
                <p className="text-sm text-gray-600 mt-1">
                  {totals.okBonds} of {totals.bonds} bonds analyzed •{' '}
                  <span className="font-semibold text-teal-700">{formatDollars(totals.totalQualified)}</span> total qualified •{' '}
                  {totals.totalInAa} properties in AA / {totals.totalOutsideAa} outside
                </p>
              )}
            </div>
            <div className="flex items-center gap-2">
              <button
                onClick={() => pitchPdfMutation.mutate()}
                disabled={pitchPdfMutation.isPending || !totals?.okBonds}
                className="flex items-center gap-1.5 text-sm font-semibold text-white bg-teal-600 hover:bg-teal-700 disabled:opacity-50 disabled:cursor-not-allowed px-3 py-1.5 rounded-md transition-colors"
              >
                <DocumentArrowDownIcon className="h-4 w-4" />
                {pitchPdfMutation.isPending ? 'Generating…' : 'Pitch PDF'}
              </button>
              <button
                onClick={handleExportCsv}
                disabled={!results || results.length === 0}
                className="flex items-center gap-1.5 text-sm font-semibold text-teal-700 bg-white border border-teal-600 hover:bg-teal-50 disabled:opacity-50 disabled:cursor-not-allowed px-3 py-1.5 rounded-md transition-colors"
                title="Export the summary table — one row per CUSIP"
              >
                <DocumentArrowDownIcon className="h-4 w-4" />
                Summary CSV
              </button>
              <button
                onClick={handleExportPropertiesCsv}
                disabled={allMapProperties.length === 0}
                className="flex items-center gap-1.5 text-sm font-semibold text-teal-700 bg-white border border-teal-600 hover:bg-teal-50 disabled:opacity-50 disabled:cursor-not-allowed px-3 py-1.5 rounded-md transition-colors"
                title={allMapProperties.length === 0
                  ? 'Switch to the Map tab once to load property detail, then export'
                  : 'Export one row per property — full qualification detail'}
              >
                <DocumentArrowDownIcon className="h-4 w-4" />
                Properties CSV
              </button>
            </div>
          </div>

          {/* Tab switcher — Table is the analyst-default; Map is the visual view */}
          <div className="flex items-center justify-between mb-4 border-b border-gray-200">
            <div className="flex items-center gap-1">
              <button
                onClick={() => setResultsView('table')}
                className={`flex items-center gap-1.5 px-4 py-2 text-sm font-medium border-b-2 -mb-px transition-colors ${
                  resultsView === 'table'
                    ? 'border-teal-600 text-teal-700'
                    : 'border-transparent text-gray-500 hover:text-gray-700'
                }`}
              >
                <TableCellsIcon className="h-4 w-4" /> Table
              </button>
              <button
                onClick={() => setResultsView('map')}
                disabled={okCusips.length === 0}
                className={`flex items-center gap-1.5 px-4 py-2 text-sm font-medium border-b-2 -mb-px transition-colors disabled:opacity-50 disabled:cursor-not-allowed ${
                  resultsView === 'map'
                    ? 'border-teal-600 text-teal-700'
                    : 'border-transparent text-gray-500 hover:text-gray-700'
                }`}
              >
                <GlobeAmericasIcon className="h-4 w-4" /> Map
                {portfolioLoading && resultsView === 'map' && (
                  <ArrowPathIcon className="h-3 w-3 animate-spin ml-1" />
                )}
              </button>
            </div>
            {filtersActive && (
              <button
                onClick={resetFilters}
                className="text-xs font-medium text-teal-700 hover:text-teal-900 mb-2"
              >
                Reset filters
              </button>
            )}
          </div>

          {/* Filter bar — applies to both Table and Map. Property-level
              filters (status / state / LMI) only do work when the map data
              has loaded. CUSIP toggles work in both views immediately. */}
          {okCusips.length > 0 && (
            <div className="mb-4 p-3 bg-gray-50 rounded-lg border border-gray-200 space-y-2">
              {/* Status chips */}
              <div className="flex items-center gap-2 flex-wrap">
                <span className="text-xs font-semibold text-gray-600 uppercase tracking-wide mr-1">Status:</span>
                {(['qualifies', 'outside_aa', 'no_affordability', 'no_geocode'] as BondMapStatus[]).map((status) => {
                  const active = filterStatuses.has(status);
                  const labels: Record<BondMapStatus, string> = {
                    qualifies: 'Qualifies',
                    outside_aa: 'Outside AA',
                    no_affordability: 'No affordability',
                    no_geocode: 'No geocode',
                  };
                  const colors: Record<BondMapStatus, string> = {
                    qualifies: active ? 'bg-green-600 text-white' : 'bg-white text-gray-700 hover:bg-green-50',
                    outside_aa: active ? 'bg-gray-500 text-white' : 'bg-white text-gray-700 hover:bg-gray-100',
                    no_affordability: active ? 'bg-amber-500 text-white' : 'bg-white text-gray-700 hover:bg-amber-50',
                    no_geocode: active ? 'bg-red-600 text-white' : 'bg-white text-gray-700 hover:bg-red-50',
                  };
                  return (
                    <button
                      key={status}
                      onClick={() => toggleStatus(status)}
                      className={`text-xs font-medium px-2.5 py-1 rounded-full border border-gray-300 transition-colors ${colors[status]}`}
                    >
                      {labels[status]}
                    </button>
                  );
                })}
              </div>

              {/* CUSIP chips + state + LMI threshold */}
              <div className="flex items-center gap-3 flex-wrap">
                <div className="flex items-center gap-2 flex-wrap">
                  <span className="text-xs font-semibold text-gray-600 uppercase tracking-wide mr-1">Bond:</span>
                  {okCusips.length > 12 ? (
                    <span className="text-xs text-gray-500">{okCusips.length} bonds — use the table to drill in</span>
                  ) : (
                    okCusips.map((cusip) => {
                      const active = filterCusips.size === 0 || filterCusips.has(cusip);
                      return (
                        <button
                          key={cusip}
                          onClick={() => toggleCusipFilter(cusip)}
                          className={`text-[11px] font-mono px-2 py-0.5 rounded border transition-colors ${
                            active
                              ? 'bg-teal-600 text-white border-teal-600'
                              : 'bg-white text-gray-500 border-gray-300 hover:bg-teal-50'
                          }`}
                        >
                          {cusip}
                        </button>
                      );
                    })
                  )}
                </div>

                {availableStates.length > 0 && (
                  <div className="flex items-center gap-1.5">
                    <span className="text-xs font-semibold text-gray-600 uppercase tracking-wide">State:</span>
                    <select
                      multiple={false}
                      value={filterStates.size === 1 ? Array.from(filterStates)[0] : ''}
                      onChange={(e) => {
                        const v = e.target.value;
                        if (!v) setFilterStates(new Set());
                        else setFilterStates(new Set([v]));
                      }}
                      className="text-xs rounded border border-gray-300 px-2 py-1"
                    >
                      <option value="">All</option>
                      {availableStates.map(s => <option key={s} value={s}>{s}</option>)}
                    </select>
                  </div>
                )}

                {(aaBoundsData?.areas?.length ?? 0) > 0 && (
                  <div className="flex items-center gap-2 flex-wrap">
                    <span className="text-xs font-semibold text-gray-600 uppercase tracking-wide">AA:</span>
                    {(aaBoundsData!.areas).slice(0, 8).map(a => {
                      const active = filterAaIds.size === 0 || filterAaIds.has(a.id);
                      return (
                        <button
                          key={a.id}
                          onClick={() => toggleAaFilter(a.id)}
                          title={`${a.branchCount} ${a.branchCount === 1 ? 'branch' : 'branches'}`}
                          className={`text-[11px] font-medium px-2 py-0.5 rounded border transition-colors ${
                            active
                              ? 'bg-teal-600 text-white border-teal-600'
                              : 'bg-white text-gray-500 border-gray-300 hover:bg-teal-50'
                          }`}
                        >
                          {a.name}
                        </button>
                      );
                    })}
                    {(aaBoundsData!.areas.length > 8) && (
                      <span className="text-[11px] text-gray-500">+{aaBoundsData!.areas.length - 8} more</span>
                    )}
                  </div>
                )}

                <div className="flex items-center gap-1.5">
                  <span className="text-xs font-semibold text-gray-600 uppercase tracking-wide">Min LMI %:</span>
                  <input
                    type="range"
                    min={0}
                    max={100}
                    step={5}
                    value={filterMinLmi}
                    onChange={(e) => setFilterMinLmi(Number(e.target.value))}
                    className="w-24"
                  />
                  <span className="text-xs font-mono w-8 text-right">{filterMinLmi}%</span>
                </div>
              </div>
            </div>
          )}

          {/* Map view */}
          {resultsView === 'map' && (
            <>
              {portfolioLoading && allMapProperties.length === 0 ? (
                <div className="h-[520px] rounded-lg border border-gray-200 flex items-center justify-center bg-gray-50">
                  <div className="flex items-center gap-2 text-gray-500">
                    <ArrowPathIcon className="h-5 w-5 animate-spin" />
                    Loading property locations…
                  </div>
                </div>
              ) : (
                <BondsResultsMap
                  properties={filteredMapProperties}
                  fallbackBounds={mapFallbackBounds}
                />
              )}
            </>
          )}

          {/* Table view */}
          {resultsView === 'table' && (
          <div className="overflow-x-auto -mx-6">
            <table className="min-w-full text-sm">
              <thead className="bg-gray-50 border-y border-gray-200">
                <tr>
                  <th className="text-left px-4 py-2 font-semibold text-gray-700">CUSIP</th>
                  <th className="text-left px-4 py-2 font-semibold text-gray-700">Status</th>
                  <th className="text-right px-4 py-2 font-semibold text-gray-700">Qualified $</th>
                  <th className="text-right px-4 py-2 font-semibold text-gray-700">In AA</th>
                  <th className="text-right px-4 py-2 font-semibold text-gray-700">Outside AA</th>
                  <th className="text-right px-4 py-2 font-semibold text-gray-700">Notes</th>
                </tr>
              </thead>
              <tbody>
                {(filteredResults ?? []).map((row) => {
                  const issues: string[] = [];
                  if (row.geocodingFailures) issues.push(`${row.geocodingFailures} geocoding`);
                  if (row.affordabilityMissing) issues.push(`${row.affordabilityMissing} no affordability`);
                  return (
                    <tr
                      key={row.cusip}
                      onClick={() => row.status === 'OK' && handleSelectRow(row.cusip)}
                      className={`border-b border-gray-100 ${
                        row.status === 'OK' ? 'cursor-pointer hover:bg-teal-50/40' : ''
                      } ${selectedCusip === row.cusip ? 'bg-teal-50' : ''}`}
                    >
                      <td className="px-4 py-2 font-mono text-xs">
                        <div>{row.cusip}</div>
                        {row.autoResolvedDealId && (
                          <div className="mt-0.5 inline-flex items-center gap-1 text-[10px] font-sans font-medium text-teal-700">
                            <SparklesIcon className="h-3 w-3" />
                            Auto-resolved · {row.autoResolvedDealId}
                          </div>
                        )}
                      </td>
                      <td className="px-4 py-2">
                        {row.status === 'OK' && (
                          <span className="inline-flex items-center gap-1 text-green-700">
                            <CheckCircleIcon className="h-4 w-4" /> OK
                          </span>
                        )}
                        {row.status === 'NOT_FOUND' && (
                          <span className="inline-flex items-center gap-2">
                            <span className="inline-flex items-center gap-1 text-amber-700">
                              <ExclamationTriangleIcon className="h-4 w-4" /> Not ingested
                            </span>
                            <button
                              onClick={(e) => {
                                e.stopPropagation();
                                setRegisterCusip(row.cusip);
                                setRegisterDealId('');
                                setRegisterModalOpen(true);
                              }}
                              className="inline-flex items-center gap-1 text-xs font-medium text-teal-700 hover:text-teal-900 underline-offset-2 hover:underline"
                            >
                              <PlusIcon className="h-3 w-3" /> Register
                            </button>
                          </span>
                        )}
                        {row.status === 'ERROR' && (
                          <span className="inline-flex items-center gap-1 text-red-700">
                            <XCircleIcon className="h-4 w-4" /> Error
                          </span>
                        )}
                      </td>
                      <td className="px-4 py-2 text-right font-semibold">
                        {row.status === 'OK' ? formatDollars(row.totalQualifiedDollars) : '—'}
                      </td>
                      <td className="px-4 py-2 text-right">
                        {row.status === 'OK' ? row.propertiesInAa : '—'}
                      </td>
                      <td className="px-4 py-2 text-right">
                        {row.status === 'OK' ? row.propertiesOutsideAa : '—'}
                      </td>
                      <td className="px-4 py-2 text-right text-xs text-gray-500">
                        {row.error ? row.error.slice(0, 60) : issues.length ? issues.join(', ') : '—'}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
          )}
        </section>
      )}

      {/* ─── Drill-down panel ─── */}
      {selectedCusip && (
        <section className="bg-white rounded-2xl border-2 border-teal-200 p-6">
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-lg font-semibold text-navy-900">
              {drillDown?.bondName ?? selectedCusip}{' '}
              <span className="font-mono text-sm text-gray-500 ml-2">{selectedCusip}</span>
            </h3>
            <button
              onClick={() => { setSelectedCusip(null); setDrillDown(null); }}
              className="text-sm text-gray-500 hover:text-gray-700"
            >
              Close
            </button>
          </div>

          {drillMutation.isPending && (
            <p className="text-sm text-gray-500">Loading detail…</p>
          )}

          {drillDown && (
            <>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
                <Stat label="Qualified $" value={formatDollars(drillDown.totalQualifiedDollars)} icon={<CurrencyDollarIcon className="h-5 w-5" />} />
                <Stat label="Properties in AA" value={String(drillDown.propertiesInAa)} icon={<MapPinIcon className="h-5 w-5" />} />
                <Stat label="Total Units in AA" value={drillDown.totalUnitsInAa.toLocaleString()} />
                <Stat label="LMI %" value={formatPct(drillDown.pctLmiUnitsInAa)} />
              </div>

              {/* Drill-down sub-tab switcher — Property table vs. compact map */}
              <div className="flex items-center gap-1 mb-3 border-b border-gray-200">
                <button
                  onClick={() => setDrillDownView('table')}
                  className={`flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium border-b-2 -mb-px transition-colors ${
                    drillDownView === 'table'
                      ? 'border-teal-600 text-teal-700'
                      : 'border-transparent text-gray-500 hover:text-gray-700'
                  }`}
                >
                  <TableCellsIcon className="h-3.5 w-3.5" /> Property table
                </button>
                <button
                  onClick={() => setDrillDownView('map')}
                  className={`flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium border-b-2 -mb-px transition-colors ${
                    drillDownView === 'map'
                      ? 'border-teal-600 text-teal-700'
                      : 'border-transparent text-gray-500 hover:text-gray-700'
                  }`}
                >
                  <GlobeAmericasIcon className="h-3.5 w-3.5" /> Map
                </button>
              </div>

              {drillDownView === 'map' ? (
                <BondsResultsMap
                  compact
                  fallbackBounds={mapFallbackBounds}
                  properties={drillDown.properties.map(p => ({
                    ...p,
                    cusip: drillDown.cusip,
                    bondName: drillDown.bondName,
                  }))}
                />
              ) : (
                <div className="overflow-x-auto -mx-6">
                  <table className="min-w-full text-sm">
                    <thead className="bg-gray-50 border-y border-gray-200">
                      <tr>
                        <th className="text-left px-4 py-2 font-semibold text-gray-700">Property</th>
                        <th className="text-left px-4 py-2 font-semibold text-gray-700">Location</th>
                        <th className="text-left px-4 py-2 font-semibold text-gray-700">AA</th>
                        <th className="text-right px-4 py-2 font-semibold text-gray-700">Units</th>
                        <th className="text-right px-4 py-2 font-semibold text-gray-700">LMI %</th>
                        <th className="text-right px-4 py-2 font-semibold text-gray-700">Allocated $</th>
                        <th className="text-left px-4 py-2 font-semibold text-gray-700">Reason</th>
                      </tr>
                    </thead>
                    <tbody>
                      {drillDown.properties.map((p) => (
                        <tr key={p.propertyId} className={`border-b border-gray-100 ${p.qualifiesForCra ? 'bg-green-50/30' : ''}`}>
                          <td className="px-4 py-2 font-medium">{p.propertyName ?? '—'}</td>
                          <td className="px-4 py-2 text-xs text-gray-600">
                            {p.city && p.state ? `${p.city}, ${p.state}` : '—'}
                            {p.county && <span className="text-gray-400"> · {p.county}</span>}
                          </td>
                          <td className="px-4 py-2 text-xs">
                            {p.insideAa ? (
                              <span className="text-green-700">{p.matchingAaName ?? 'Inside AA'}</span>
                            ) : (
                              <span className="text-gray-400">Outside</span>
                            )}
                          </td>
                          <td className="px-4 py-2 text-right">{p.units?.toLocaleString() ?? '—'}</td>
                          <td className="px-4 py-2 text-right">{formatPct(p.lmiPct)}</td>
                          <td className="px-4 py-2 text-right font-semibold">{formatDollars(p.allocatedDollars)}</td>
                          <td className="px-4 py-2 text-xs text-gray-600">{p.reason}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </>
          )}
        </section>
      )}

      {/* ─── Register CUSIP modal ─── */}
      {registerModalOpen && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center bg-black/30"
          onClick={() => setRegisterModalOpen(false)}
        >
          <div
            className="bg-white rounded-2xl p-6 w-full max-w-md mx-4 shadow-xl"
            onClick={e => e.stopPropagation()}
          >
            <h3 className="text-lg font-semibold text-navy-900 mb-2">Register CUSIP manually</h3>
            <p className="text-sm text-gray-600 mb-4">
              OpenFIGI couldn't auto-map this CUSIP. Enter the Deal ID manually so we can
              pull the OCS prospectus and run analysis. For Freddie Multi PCs, the Deal ID
              looks like <span className="font-mono">WN5391</span> (find it via MSIA Quick
              Search → PC/Giant Deals tab).
            </p>
            <form
              onSubmit={(e) => {
                e.preventDefault();
                if (!registerCusip.trim() || !registerDealId.trim()) return;
                registerMutation.mutate();
              }}
              className="space-y-3"
            >
              <div>
                <label className="block text-xs font-medium text-gray-700 mb-1">CUSIP</label>
                <input
                  type="text"
                  value={registerCusip}
                  onChange={e => setRegisterCusip(e.target.value.toUpperCase())}
                  placeholder="3132XK7D3"
                  maxLength={9}
                  className="w-full rounded-lg border border-gray-300 px-3 py-2 font-mono text-sm focus:outline-none focus:ring-2 focus:ring-teal-500 focus:border-teal-500"
                  autoFocus
                />
              </div>
              <div>
                <label className="block text-xs font-medium text-gray-700 mb-1">Deal ID</label>
                <input
                  type="text"
                  value={registerDealId}
                  onChange={e => setRegisterDealId(e.target.value)}
                  placeholder="WN5391"
                  className="w-full rounded-lg border border-gray-300 px-3 py-2 font-mono text-sm focus:outline-none focus:ring-2 focus:ring-teal-500 focus:border-teal-500"
                />
              </div>
              <div className="flex gap-2 justify-end pt-2">
                <button
                  type="button"
                  onClick={() => setRegisterModalOpen(false)}
                  className="px-4 py-2 text-sm text-gray-700 hover:text-gray-900"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={registerMutation.isPending || !registerCusip.trim() || !registerDealId.trim()}
                  className="bg-teal-600 hover:bg-teal-700 disabled:opacity-50 disabled:cursor-not-allowed text-white px-4 py-2 rounded-lg text-sm font-semibold"
                >
                  {registerMutation.isPending ? 'Registering…' : 'Register & Pull OCS'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* ─── Sidebar: Recent lookups ─── */}
      {lookupsData?.lookups?.length > 0 && !results && (
        <section className="bg-white rounded-2xl border border-gray-200 p-6">
          <h2 className="text-lg font-semibold text-navy-900 mb-2">Recent lookups</h2>
          <p className="text-xs text-gray-500 mb-4">Click to reload — no recompute, just opens the saved results.</p>
          <ul className="divide-y divide-gray-100">
            {lookupsData.lookups.slice(0, 5).map((l: any) => (
              <li
                key={l.id}
                onClick={() => reloadMutation.mutate(l.id)}
                className="py-3 flex items-center justify-between text-sm cursor-pointer hover:bg-teal-50/40 -mx-6 px-6 rounded transition-colors"
              >
                <div>
                  <p className="font-medium text-navy-900">{l.label || `${l.cusips.length} CUSIPs`}</p>
                  <p className="text-xs text-gray-500">
                    {new Date(l.requestedAt).toLocaleString()}
                    {' · '}
                    {l.user?.firstName ?? l.user?.email}
                  </p>
                </div>
                <span className={`text-xs font-medium px-2 py-1 rounded ${
                  l.status === 'COMPLETED' ? 'bg-green-100 text-green-800' :
                  l.status === 'PARTIAL'   ? 'bg-amber-100 text-amber-800' :
                  l.status === 'FAILED'    ? 'bg-red-100 text-red-800' :
                                              'bg-gray-100 text-gray-700'
                }`}>{l.status}</span>
              </li>
            ))}
          </ul>
        </section>
      )}
    </div>
  );
}

// ─── Sub-component ──────────────────────────────────────────────────────────

function Stat({ label, value, icon }: { label: string; value: string; icon?: React.ReactNode }) {
  return (
    <div className="bg-gray-50 rounded-lg p-4">
      <div className="flex items-center gap-2 text-xs text-gray-500 uppercase tracking-wide mb-1">
        {icon}
        {label}
      </div>
      <div className="text-xl font-bold text-navy-900">{value}</div>
    </div>
  );
}
